import { useState } from 'react';
import { format } from 'date-fns';
import { ChevronDown, ChevronUp, MapPin, User, Building2, FileText } from 'lucide-react';
import { Badge } from '../ui/badge';

/** Compact project header: ref + badges + who/where on one line, extras under "Details". */
export function ProjectHeader({ project, typeColors, statusColors, children }) {
  const [open, setOpen] = useState(false);
  const address = [project.address_line1, project.town_city, project.postcode].filter(Boolean).join(', ');
  return (
    <div className="rounded-lg border border-white/10 bg-[#2A3240] px-5 py-4" data-testid="project-detail-header">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1
              className="text-2xl md:text-3xl font-bold text-[#F5A623] leading-none"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              data-testid="project-ref-header"
            >
              {project.project_ref}
            </h1>
            <Badge className={`${typeColors[project.project_type] || typeColors.DEV} text-xs`}>{project.project_type || 'DEV'}</Badge>
            <Badge className={statusColors[project.status] || statusColors.Live}>{project.status}</Badge>
            {project.primary_project_type && (
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">{project.primary_project_type}</Badge>
            )}
            {project.project_source && (
              <Badge variant="outline" className="text-xs border-white/20 text-gray-400">{project.project_source}</Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2.5 text-sm text-gray-300">
            <span className="inline-flex items-center gap-1.5" data-testid="header-client"><User className="w-3.5 h-3.5 text-gray-500" />{project.client_name || '—'}</span>
            <span className="inline-flex items-center gap-1.5" data-testid="header-address"><MapPin className="w-3.5 h-3.5 text-gray-500" />{address || '—'}</span>
            {project.architect_name && <span className="inline-flex items-center gap-1.5" data-testid="header-architect"><Building2 className="w-3.5 h-3.5 text-gray-500" />{project.architect_name}</span>}
            {project.contact_name && project.contact_name !== project.client_name && <span className="inline-flex items-center gap-1.5" data-testid="header-contractor"><FileText className="w-3.5 h-3.5 text-gray-500" />{project.contact_name}</span>}
            <button
              type="button"
              onClick={() => setOpen(o => !o)}
              className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white"
              data-testid="header-details-toggle"
            >
              Details {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>
          {open && (
            <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs" data-testid="header-details">
              <div><div className="text-gray-500">Client contact</div><div className="text-gray-200">{project.telephone || '—'}{project.email ? <><br />{project.email}</> : null}</div></div>
              <div><div className="text-gray-500">Created</div><div className="text-gray-200">{format(new Date(project.created_at), 'dd MMM yyyy')}{project.created_by_name ? ` · ${project.created_by_name}` : ''}</div></div>
              <div className="sm:col-span-2"><div className="text-gray-500">Project notes</div><div className="text-gray-200 whitespace-pre-wrap">{project.notes || '—'}</div></div>
            </div>
          )}
        </div>
        {children && <div className="flex items-center gap-2 flex-shrink-0">{children}</div>}
      </div>
    </div>
  );
}
